// fin_invoice — the charge (ADR-000 §6.3 Invoice state machine: due → partial →
// paid, due → void). Money is integer paise; dates are epoch-ms INTEGER. The
// invoice's cross-module reference (module/refType/refId) is LOOSE — an opaque
// pointer resolved through the injected chargeSources seam, never a cross-DB FK.
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs, PurposeCode } from "@erp/core-schemas";

export const INVOICE_STATUSES = ["due", "partial", "paid", "void"] as const;
export const InvoiceStatus = z.enum(INVOICE_STATUSES);
export type InvoiceStatus = z.infer<typeof InvoiceStatus>;

/** Positive integer paise — the wire-side half of the two-layer amount rule (the service re-checks). */
export const PositivePaise = z.number().int().positive();

/**
 * POST /invoices (staff, fin:manage). TWO creation paths, one schema:
 *   • module 'general' — free-form: the staff supply amount/description/payer.
 *   • module '<m>' + refType + refId — the AUTHORITATIVE amount/description/payer
 *     come from the injected chargeSources[m] resolver (no client-supplied amount
 *     is honored on this path; unknown module ⇒ FIN_CHARGE_SOURCE_UNKNOWN, null
 *     resolution ⇒ FIN_CHARGE_REF_NOT_FOUND).
 */
export const CreateFinInvoice = z
  .object({
    module: z.string().trim().min(1).max(16).default("general"),
    refType: z.string().trim().min(1).max(40).optional(),
    refId: EntityId.optional(),
    payerUserId: EntityId.optional(),
    payerName: z.string().trim().min(1).max(160).optional(),
    payerContact: z.string().trim().min(1).max(160).optional(),
    description: z.string().trim().min(1).max(500).optional(),
    amountPaise: PositivePaise.optional(),
    dueDate: EpochMs.optional(),
    /**
     * What the money is FOR (ADR-008 §3). Optional on the wire because a module
     * raising a charge through the chargeSources seam supplies it server-side,
     * and because the general path has no module to ask — but the SERVICE
     * refuses a code the merged registry does not know, so a typo never reaches
     * a row and never becomes an accounting category nobody can classify.
     */
    purposeCode: PurposeCode.optional(),
  })
  .refine((v) => (v.module === "general" ? v.amountPaise !== undefined && v.description !== undefined && v.payerName !== undefined : true), {
    message: "a general invoice requires amountPaise, description and payerName",
    path: ["amountPaise"],
  })
  .refine((v) => (v.module !== "general" ? v.refType !== undefined && v.refId !== undefined : true), {
    message: "a module invoice requires refType and refId (the chargeSources seam resolves the amount)",
    path: ["refId"],
  });
export type CreateFinInvoice = z.infer<typeof CreateFinInvoice>;

/** Keyset list + the console facets. `search` matches invoice_no / payer_name (prefix-friendly LIKE). */
export const InvoiceListQuery = CursorQuery.extend({
  status: InvoiceStatus.optional(),
  module: z.string().trim().min(1).max(16).optional(),
  search: z.string().trim().min(1).max(120).optional(),
  payer: EntityId.optional(),
});
export type InvoiceListQuery = z.infer<typeof InvoiceListQuery>;

/** POST /invoices/:id/void — allowed only while NO verified payment exists (FIN_INVOICE_HAS_PAYMENTS otherwise). */
export const VoidInvoiceBody = z.object({
  note: z.string().trim().min(1).max(500).optional(),
});
export type VoidInvoiceBody = z.infer<typeof VoidInvoiceBody>;

export const FinInvoiceDto = z.object({
  id: EntityId,
  invoiceNo: z.string(),
  module: z.string(),
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  /** The FROZEN code. `null` on rows written before 0003 — unclassified, never guessed. */
  purposeCode: z.string().nullable(),
  /** Display only, resolved from the merged registry at read; falls back to the raw code. */
  purposeLabel: z.string().nullable(),
  payerUserId: z.string().nullable(),
  payerName: z.string(),
  payerContact: z.string().nullable(),
  description: z.string(),
  amountPaise: z.number().int(),
  /** Σ verified payments so far — derived at read, never stored. */
  paidPaise: z.number().int(),
  /** amountPaise − paidPaise, floored at 0 (overpay closes at 'paid', recorded as-is). */
  outstandingPaise: z.number().int(),
  status: InvoiceStatus,
  dueDate: EpochMs.nullable(),
  // ── provenance (db-fin 0002) — the "last edited by" a console row shows ──
  createdBy: z.string().nullable(),
  createdAt: EpochMs,
  updatedAt: EpochMs.nullable(),
  updatedBy: z.string().nullable(),
});
export type FinInvoiceDto = z.infer<typeof FinInvoiceDto>;

export const LEDGER_ENTRIES = ["charge", "payment", "credit_note", "void"] as const;
export const LedgerEntry = z.enum(LEDGER_ENTRIES);
export type LedgerEntry = z.infer<typeof LedgerEntry>;

/** fin_ledger — APPEND-ONLY (no UPDATE/DELETE path exists anywhere; corrections are credit notes). */
export const FinLedgerDto = z.object({
  id: EntityId,
  at: EpochMs,
  invoiceId: EntityId,
  paymentId: EntityId.nullable(),
  entry: LedgerEntry,
  amountPaise: z.number().int(),
  note: z.string().nullable(),
  actor: z.string().nullable(),
});
export type FinLedgerDto = z.infer<typeof FinLedgerDto>;

/** GET /ledger — the console's append-only journal view (keyset on (at, id) DESC). */
export const LedgerListQuery = CursorQuery.extend({
  invoiceId: EntityId.optional(),
});
export type LedgerListQuery = z.infer<typeof LedgerListQuery>;
